'use client';

import * as React from 'react';
import { createPortal } from 'react-dom';
import NextImage from 'next/image';
import { gsap } from 'gsap';
import { ChevronLeft, ChevronRight, Play, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import CmsImage from '@/components/ui/CmsImage';
import { useModalScrollLock } from '@/hooks/useModalScrollLock';
import { getImageUrl, getImageUrlForVariant } from '@/lib/homepage-mappers';
import { MEDIA_PLACEHOLDER } from '@/lib/media-placeholder';
import type { StrapiFlatMedia } from '@/lib/strapi-media-url';
import { cn } from '@/utils/cn';
import type { VideoTestimonialZone } from '@/types/homepage';
import type { TestimonialComponent } from '@/types/product';

type TestimonialZone = VideoTestimonialZone | TestimonialComponent;

export type VoicesOfTrustTheme = 'light' | 'dark';

interface HomeVoicesOfTrustShowcaseProps {
  testimonialComponent?: TestimonialZone;
  /** Prefer this over manual `styles` overrides for light (home) vs dark (products). */
  theme?: VoicesOfTrustTheme;
  styles?: HomeVoicesOfTrustShowcaseStyles;
}

export type HomeVoicesOfTrustShowcaseStyles = {
  sectionClassName?: string;
  headingClassName?: string;
  mediaAccentClassName?: string;
  mediaGlowClassName?: string;
  mediaFrameClassName?: string;
  quoteMarkClassName?: string;
  quoteIconClassName?: string;
  quoteClassName?: string;
  expandButtonClassName?: string;
  authorNameClassName?: string;
  authorRoleClassName?: string;
  authorRoleSeparatorClassName?: string;
  authorCompanyClassName?: string;
  controlButtonClassName?: string;
  inactiveDotClassName?: string;
  counterClassName?: string;
};

type ShowcaseSlide = {
  id: string;
  name: string;
  role: string;
  quoteHtml: string;
  image: string;
  imageAlt: string;
  videoUrl: string;
  isVideo: boolean;
  media: StrapiFlatMedia | null;
};

const FALLBACK_IMAGE = '/images/home_img1.png';
const SLIDE_INTERVAL_MS = 7000;
/** Collapsed quote height: 4 lines × 1.65 line-height (em of 20px text). */
const QUOTE_COLLAPSED_MAX_HEIGHT = `${4 * 1.65}em`;

const THEME_STYLES: Record<VoicesOfTrustTheme, HomeVoicesOfTrustShowcaseStyles> = {
  light: {
    sectionClassName: 'bg-white text-[#151515]',
    headingClassName: 'text-[#151515]',
    mediaAccentClassName: 'bg-[#E82127]/10',
    mediaGlowClassName: 'bg-black/5',
    mediaFrameClassName: 'bg-[#151515] shadow-[0_26px_70px_rgba(0,0,0,0.16)]',
    quoteMarkClassName: 'text-[#E82127]',
    quoteClassName: 'text-[#151515]',
    expandButtonClassName: 'text-[#E82127] hover:text-[#C91622]',
    authorNameClassName: 'text-[#151515]',
    authorRoleClassName: 'text-[#6D6D6D]',
    authorRoleSeparatorClassName: 'text-[#B8B8B8]',
    authorCompanyClassName: 'text-[#E82127]',
    controlButtonClassName:
      'border-[#E5E5E5] bg-white text-[#6F6F6F] shadow-sm hover:bg-[#F7F7F7] hover:text-[#151515]',
    inactiveDotClassName: 'bg-[#D9D9D9]',
    counterClassName: 'text-[#B9B9B9]',
  },
  dark: {
    sectionClassName: 'bg-black text-white',
    headingClassName: 'text-white',
    mediaAccentClassName: 'bg-[#E82127]/25',
    mediaGlowClassName: 'bg-white/10',
    mediaFrameClassName: 'bg-zinc-900 shadow-[0_26px_70px_rgba(0,0,0,0.45)]',
    quoteMarkClassName: 'text-[#E82127]',
    quoteIconClassName: 'opacity-80',
    quoteClassName: 'text-white/78 [&_b]:text-white [&_strong]:text-white',
    expandButtonClassName: 'text-[#FF5A5F] hover:text-white',
    authorNameClassName: 'text-white',
    authorRoleClassName: 'text-white/62',
    authorRoleSeparatorClassName: 'text-white/30',
    authorCompanyClassName: 'text-[#E82127]',
    controlButtonClassName:
      'border-white/15 bg-white/10 text-white shadow-none hover:bg-white hover:text-[#151515]',
    inactiveDotClassName: 'bg-white/65 shadow-[0_0_0_1px_rgba(255,255,255,0.18)]',
    counterClassName: 'text-white/45',
  },
};

const SAMPLE_SLIDES: ShowcaseSlide[] = [
  {
    id: 'sample-testimonial-1',
    name: 'Mr. Harish Srivastava',
    role: 'Senior Civil Engineer · Srivastava & Associates',
    quoteHtml:
      'Wonder Cement has been the backbone of every project I have taken up in the last decade. The consistency in quality and the finish it delivers is unmatched. My clients notice the difference, and so do I.',
    image: '/images/home_img1.png',
    imageAlt: 'Wonder Cement project showcase',
    videoUrl: '',
    isVideo: false,
    media: null,
  },
  {
    id: 'sample-testimonial-2',
    name: 'Ms. Ananya Mehta',
    role: 'Architect · Mehta Design Studio',
    quoteHtml:
      'For residential builds, the material quality makes a visible difference. Wonder Cement gives our sites dependable strength, a smoother finish, and confidence that the structure will perform beautifully over time.',
    image: '/images/slider-img.png',
    imageAlt: 'Wonder Cement construction showcase',
    videoUrl: '',
    isVideo: false,
    media: null,
  },
  {
    id: 'sample-testimonial-3',
    name: 'Mr. Vikram Patel',
    role: 'Contractor · Patel Buildcon',
    quoteHtml:
      'We prefer Wonder Cement because the mix performs consistently across batches. It helps our teams work faster without compromising the final result, especially on projects where deadlines are tight.',
    image: '/images/home_img3.png',
    imageAlt: 'Wonder Cement community showcase',
    videoUrl: '',
    isVideo: false,
    media: null,
  },
];

function splitRole(role: string | null | undefined) {
  const safeRole = role ?? '';
  const [title, company] = safeRole.split(/\s+[·|]\s+|\s+-\s+/);

  return {
    title: title?.trim() || safeRole,
    company: company?.trim() || '',
  };
}

function looksLikeVideo(url: string, mime?: string | null) {
  return Boolean(mime?.startsWith('video/') || /\.(mp4|webm|mov)(\?|$)/i.test(url));
}

function isVideoTestimonialZone(component: TestimonialZone): component is VideoTestimonialZone {
  return component.__component === 'common.video-testimonial';
}

function formatAuthorRole(
  designation?: string | null,
  authorCompany?: string | null,
  legacyRole?: string | null,
) {
  const title = designation?.trim() || legacyRole?.trim() || '';
  const company = authorCompany?.trim() || '';
  if (title && company) return `${title} · ${company}`;
  return title || company;
}

function toFlatMedia(media: unknown): StrapiFlatMedia | null {
  if (!media || typeof media !== 'object') return null;
  return media as StrapiFlatMedia;
}

function mergeShowcaseStyles(
  base: HomeVoicesOfTrustShowcaseStyles,
  stylesOverride?: HomeVoicesOfTrustShowcaseStyles,
): HomeVoicesOfTrustShowcaseStyles {
  if (!stylesOverride) return base;

  return {
    ...base,
    ...stylesOverride,
    // Class overrides should extend the theme, not replace it.
    sectionClassName: cn(base.sectionClassName, stylesOverride.sectionClassName),
    headingClassName: cn(base.headingClassName, stylesOverride.headingClassName),
    mediaAccentClassName: cn(base.mediaAccentClassName, stylesOverride.mediaAccentClassName),
    mediaGlowClassName: cn(base.mediaGlowClassName, stylesOverride.mediaGlowClassName),
    mediaFrameClassName: cn(base.mediaFrameClassName, stylesOverride.mediaFrameClassName),
    quoteMarkClassName: cn(base.quoteMarkClassName, stylesOverride.quoteMarkClassName),
    quoteIconClassName: cn(base.quoteIconClassName, stylesOverride.quoteIconClassName),
    quoteClassName: cn(base.quoteClassName, stylesOverride.quoteClassName),
    expandButtonClassName: cn(base.expandButtonClassName, stylesOverride.expandButtonClassName),
    authorNameClassName: cn(base.authorNameClassName, stylesOverride.authorNameClassName),
    authorRoleClassName: cn(base.authorRoleClassName, stylesOverride.authorRoleClassName),
    authorRoleSeparatorClassName: cn(
      base.authorRoleSeparatorClassName,
      stylesOverride.authorRoleSeparatorClassName,
    ),
    authorCompanyClassName: cn(base.authorCompanyClassName, stylesOverride.authorCompanyClassName),
    controlButtonClassName: cn(base.controlButtonClassName, stylesOverride.controlButtonClassName),
    inactiveDotClassName: cn(base.inactiveDotClassName, stylesOverride.inactiveDotClassName),
    counterClassName: cn(base.counterClassName, stylesOverride.counterClassName),
  };
}

function mapVideoTestimonialSlide(testimonial: any, index: number): ShowcaseSlide {
  const media = toFlatMedia(testimonial.testimonialVideo);
  const mediaUrl = getImageUrl(testimonial.testimonialVideo) || '';
  const isVideo = looksLikeVideo(mediaUrl, media?.mime);

  return {
    id: `testimonial-${testimonial.id ?? index}`,
    name: testimonial.author || 'Wonder Cement Customer',
    role: formatAuthorRole(
      testimonial.designation,
      testimonial.authorCompany,
      testimonial.aboutAuthor,
    ),
    quoteHtml: testimonial.testimonial ?? testimonial.content ?? '',
    image: mediaUrl || FALLBACK_IMAGE,
    imageAlt: media?.alternativeText || testimonial.author || 'Wonder Cement customer',
    videoUrl: isVideo ? mediaUrl : '',
    isVideo,
    media,
  };
}

function mapAuthorTestimonialSlide(testimonial: any, index: number): ShowcaseSlide {
  const authorMedia = testimonial.authorImage;
  const media = toFlatMedia(authorMedia);
  const mediaUrl =
    getImageUrlForVariant(authorMedia as Parameters<typeof getImageUrlForVariant>[0], 'medium') ||
    getImageUrl(authorMedia as Parameters<typeof getImageUrl>[0]) ||
    '';
  const isVideo = looksLikeVideo(mediaUrl, media?.mime);

  return {
    id: `testimonial-${testimonial.id ?? index}`,
    name: testimonial.author || 'Wonder Cement Customer',
    role: formatAuthorRole(undefined, undefined, testimonial.aboutAuthor),
    quoteHtml: testimonial.content ?? '',
    image: mediaUrl || FALLBACK_IMAGE,
    imageAlt: media?.alternativeText || testimonial.author || 'Wonder Cement customer',
    videoUrl: isVideo ? mediaUrl : '',
    isVideo,
    media,
  };
}

function mapCmsSlides(testimonialComponent: TestimonialZone): ShowcaseSlide[] {
  if (isVideoTestimonialZone(testimonialComponent)) {
    return testimonialComponent.singleTestimonial.map(mapVideoTestimonialSlide);
  }

  return testimonialComponent.singleTestimonial.map(mapAuthorTestimonialSlide);
}

function slidesFingerprint(slides: ShowcaseSlide[]) {
  return slides.map((slide) => slide.id).join('|');
}

/** Cross-fade the active testimonial media layer in and the previous one out. */
function runMediaTransition(args: {
  mediaRefs: React.MutableRefObject<Array<HTMLDivElement | null>>;
  mediaLayerRefs: React.MutableRefObject<Array<HTMLDivElement | null>>;
  prevActiveIndexRef: React.MutableRefObject<number>;
  hasMediaAnimatedRef: React.MutableRefObject<boolean>;
  activeIndex: number;
}): void {
  const { mediaRefs, mediaLayerRefs, prevActiveIndexRef, hasMediaAnimatedRef, activeIndex } = args;

  const mediaItems = mediaRefs.current.filter(Boolean) as HTMLDivElement[];
  const mediaLayers = mediaLayerRefs.current.filter(Boolean) as HTMLDivElement[];
  if (!mediaItems.length || !mediaLayers.length) return;

  const previousIndex = prevActiveIndexRef.current;
  const activeMedia = mediaRefs.current[activeIndex];
  const activeLayer = mediaLayerRefs.current[activeIndex];

  if (!hasMediaAnimatedRef.current) {
    hasMediaAnimatedRef.current = true;
    mediaItems.forEach((item, index) => {
      gsap.set(item, { opacity: index === activeIndex ? 1 : 0 });
    });
    mediaLayers.forEach((layer) => {
      gsap.set(layer, { scale: 1, transformOrigin: 'center center' });
    });
    prevActiveIndexRef.current = activeIndex;
    return;
  }

  if (previousIndex === activeIndex) return;

  gsap.killTweensOf([...mediaItems, ...mediaLayers]);

  const previousMedia = mediaRefs.current[previousIndex];
  if (previousMedia) {
    gsap.to(previousMedia, {
      opacity: 0,
      duration: 0.42,
      ease: 'power2.inOut',
    });
  }

  if (activeMedia && activeLayer) {
    gsap.set(activeMedia, { opacity: 0 });
    gsap.fromTo(
      activeLayer,
      { scale: 1.06 },
      {
        scale: 1,
        duration: 0.62,
        ease: 'power2.out',
        transformOrigin: 'center center',
      },
    );
    gsap.to(activeMedia, {
      opacity: 1,
      duration: 0.62,
      ease: 'power2.out',
    });
  }

  mediaItems.forEach((item, index) => {
    if (index === activeIndex || index === previousIndex) return;
    gsap.set(item, { opacity: 0 });
  });

  mediaLayers.forEach((layer, index) => {
    if (index === activeIndex) return;
    gsap.set(layer, { scale: 1 });
  });

  prevActiveIndexRef.current = activeIndex;
}

/** Section title from CMS (video vs author zone), or default before mount. */
function resolveShowcaseTitle(
  testimonialComponent: TestimonialZone | undefined,
  isMounted: boolean,
): string {
  if (!isMounted || !testimonialComponent) return 'Voices of Trust';
  return isVideoTestimonialZone(testimonialComponent)
    ? testimonialComponent.heading
    : testimonialComponent.title;
}

/** Quote decoration image URL from the correct CMS field per zone type. */
function resolveShowcaseQuoteIconUrl(
  testimonialComponent: TestimonialZone | undefined,
  isMounted: boolean,
): string | null {
  if (!isMounted || !testimonialComponent) return null;
  if (isVideoTestimonialZone(testimonialComponent)) {
    return getImageUrl(testimonialComponent.quoteImage);
  }
  return getImageUrl(
    testimonialComponent.testimonialQuoteImage as Parameters<typeof getImageUrl>[0],
  );
}

/** Alt text for the quote decoration image per zone type. */
function resolveShowcaseQuoteIconAlt(testimonialComponent: TestimonialZone | undefined): string {
  if (!testimonialComponent) return '';
  return isVideoTestimonialZone(testimonialComponent)
    ? testimonialComponent.quoteImage?.alternativeText || ''
    : testimonialComponent.testimonialQuoteImage?.alternativeText || '';
}

export default function HomeVoicesOfTrustShowcase({
  testimonialComponent,
  theme = 'light',
  styles: stylesOverride,
}: HomeVoicesOfTrustShowcaseProps) {
  const styles = React.useMemo(
    () => mergeShowcaseStyles(THEME_STYLES[theme], stylesOverride),
    [theme, stylesOverride],
  );

  const [activeIndex, setActiveIndex] = React.useState(0);
  const [isMounted, setIsMounted] = React.useState(false);
  const [isLightboxOpen, setIsLightboxOpen] = React.useState(false);
  const [isQuoteExpanded, setIsQuoteExpanded] = React.useState(false);
  const [canExpandQuote, setCanExpandQuote] = React.useState(false);
  const [isCarouselHovered, setIsCarouselHovered] = React.useState(false);
  const mediaRefs = React.useRef<Array<HTMLDivElement | null>>([]);
  const mediaLayerRefs = React.useRef<Array<HTMLDivElement | null>>([]);
  const lightboxVideoRef = React.useRef<HTMLVideoElement | null>(null);
  const prevActiveIndexRef = React.useRef(0);
  const hasMediaAnimatedRef = React.useRef(false);
  const slidesFingerprintRef = React.useRef('');
  const quoteMarkRef = React.useRef<HTMLDivElement | null>(null);
  const quoteMotionRef = React.useRef<HTMLDivElement | null>(null);
  const quoteRef = React.useRef<HTMLDivElement | null>(null);
  const authorRef = React.useRef<HTMLDivElement | null>(null);
  const controlsRef = React.useRef<HTMLDivElement | null>(null);

  const closeLightbox = React.useCallback(() => {
    const lightboxVideo = lightboxVideoRef.current;
    if (lightboxVideo) {
      lightboxVideo.pause();
      lightboxVideo.currentTime = 0;
    }
    setIsLightboxOpen(false);
  }, []);

  React.useEffect(() => {
    setIsMounted(true);
  }, []);

  useModalScrollLock(isLightboxOpen, closeLightbox);

  const slides = React.useMemo(() => {
    if (
      isMounted &&
      testimonialComponent?.singleTestimonial &&
      testimonialComponent.singleTestimonial.length > 0
    ) {
      return mapCmsSlides(testimonialComponent);
    }

    return SAMPLE_SLIDES;
  }, [isMounted, testimonialComponent]);

  const sectionTitle = React.useMemo(
    () => resolveShowcaseTitle(testimonialComponent, isMounted),
    [isMounted, testimonialComponent],
  );

  const quoteIconUrl = React.useMemo(
    () => resolveShowcaseQuoteIconUrl(testimonialComponent, isMounted),
    [isMounted, testimonialComponent],
  );

  const quoteIconAlt = React.useMemo(
    () => resolveShowcaseQuoteIconAlt(testimonialComponent),
    [testimonialComponent],
  );

  const totalSlides = slides.length;
  const activeSlide = slides[activeIndex] ?? slides[0];
  const hasMultipleSlides = totalSlides > 1;
  const roleParts = splitRole(activeSlide?.role);
  const currentFingerprint = slidesFingerprint(slides);

  const goToSlide = React.useCallback(
    (nextIndex: number) => {
      if (totalSlides <= 0) return;
      setActiveIndex((nextIndex + totalSlides) % totalSlides);
    },
    [totalSlides],
  );

  React.useEffect(() => {
    if (activeIndex <= totalSlides - 1) return;
    setActiveIndex(0);
  }, [activeIndex, totalSlides]);

  React.useEffect(() => {
    if (!hasMultipleSlides || isCarouselHovered || isLightboxOpen) return;

    const timer = window.setTimeout(() => {
      goToSlide(activeIndex + 1);
    }, SLIDE_INTERVAL_MS);

    return () => window.clearTimeout(timer);
  }, [activeIndex, goToSlide, hasMultipleSlides, isCarouselHovered, isLightboxOpen]);

  React.useEffect(() => {
    closeLightbox();
    setIsQuoteExpanded(false);
    setCanExpandQuote(false);
  }, [activeIndex, closeLightbox]);

  // Remount-safe media opacity when CMS slides replace sample slides.
  React.useEffect(() => {
    if (slidesFingerprintRef.current === currentFingerprint) return;
    slidesFingerprintRef.current = currentFingerprint;
    hasMediaAnimatedRef.current = false;
    prevActiveIndexRef.current = activeIndex;
  }, [activeIndex, currentFingerprint]);

  React.useEffect(() => {
    if (isQuoteExpanded) return;

    const quoteElement = quoteRef.current;
    if (!quoteElement) return;

    const measureOverflow = () => {
      setCanExpandQuote(quoteElement.scrollHeight > quoteElement.clientHeight + 1);
    };

    let frame2 = 0;
    const frame1 = window.requestAnimationFrame(() => {
      frame2 = window.requestAnimationFrame(measureOverflow);
    });

    const resizeObserver = new ResizeObserver(measureOverflow);
    resizeObserver.observe(quoteElement);

    return () => {
      window.cancelAnimationFrame(frame1);
      window.cancelAnimationFrame(frame2);
      resizeObserver.disconnect();
    };
  }, [activeIndex, isQuoteExpanded, currentFingerprint]);

  React.useEffect(() => {
    runMediaTransition({
      mediaRefs,
      mediaLayerRefs,
      prevActiveIndexRef,
      hasMediaAnimatedRef,
      activeIndex,
    });
  }, [activeIndex, currentFingerprint]);

  React.useEffect(() => {
    const sequence = [
      quoteMarkRef.current,
      quoteMotionRef.current,
      authorRef.current,
      controlsRef.current,
    ].filter(Boolean) as HTMLElement[];
    if (!sequence.length) return;

    gsap.killTweensOf(sequence);
    gsap.set(sequence, { y: 28, opacity: 0 });
    gsap.to(sequence, {
      y: 0,
      opacity: 1,
      duration: 0.64,
      ease: 'power3.out',
      stagger: 0.1,
    });
  }, [activeIndex]);

  const openLightbox = React.useCallback(() => {
    if (!activeSlide?.isVideo || !activeSlide.videoUrl) return;
    setIsLightboxOpen(true);
  }, [activeSlide?.isVideo, activeSlide?.videoUrl]);

  React.useEffect(() => {
    if (!isLightboxOpen) return;

    const frame = window.requestAnimationFrame(() => {
      const lightboxVideo = lightboxVideoRef.current;
      if (!lightboxVideo) return;
      lightboxVideo.currentTime = 0;
      lightboxVideo.play().catch(console.error);
    });

    return () => window.cancelAnimationFrame(frame);
  }, [isLightboxOpen, activeSlide?.videoUrl]);

  if (!activeSlide) return null;

  return (
    <section className={cn(styles.sectionClassName)}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-0">
        <h2
          className={cn(
            'text-center text-[2.25rem] font-bold leading-tight tracking-normal sm:text-5xl lg:text-[4rem]',
            styles.headingClassName,
          )}
        >
          {sectionTitle}
        </h2>

        <div
          className="mt-10 grid items-center gap-9 lg:mt-16 lg:grid-cols-[minmax(0,0.94fr)_minmax(420px,0.82fr)] lg:gap-16 xl:gap-24"
          onMouseEnter={() => setIsCarouselHovered(true)}
          onMouseLeave={() => setIsCarouselHovered(false)}
        >
          <div className="relative mx-auto w-full max-w-[640px] lg:mx-0">
            <div
              className={cn(
                'absolute -bottom-3 -right-3 hidden h-28 w-28 rounded-[18px] sm:block',
                styles.mediaAccentClassName,
              )}
            />
            <div
              className={cn(
                'absolute inset-0 translate-y-8 rounded-[28px] blur-3xl',
                styles.mediaGlowClassName,
              )}
            />

            <div
              className={cn(
                'relative aspect-[16/9] overflow-hidden rounded-[24px] sm:rounded-[28px]',
                styles.mediaFrameClassName,
              )}
            >
              {slides.map((slide, index) => {
                const visible = index === activeIndex;

                return (
                  <div
                    key={slide.id}
                    ref={(element) => {
                      mediaRefs.current[index] = element;
                    }}
                    className={cn(
                      'absolute inset-0 will-change-opacity',
                      visible ? 'z-10' : 'pointer-events-none z-0',
                    )}
                    aria-hidden={!visible}
                  >
                    <div
                      ref={(element) => {
                        mediaLayerRefs.current[index] = element;
                      }}
                      className="absolute inset-0 overflow-hidden will-change-transform"
                    >
                      <CmsImage
                        src={slide.image}
                        media={slide.media}
                        alt={slide.imageAlt}
                        fill
                        priority={index === 0}
                        deferVideoLoad
                        className="object-cover object-center"
                        sizes="(min-width: 1280px) 590px, (min-width: 1024px) 50vw, 100vw"
                        fallbackSrc={MEDIA_PLACEHOLDER}
                        // Show video frame on the card; playback only in the lightbox.
                        autoPlay={false}
                        muted
                        loop={false}
                        playsInline
                      />

                      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/18 via-transparent to-transparent" />
                    </div>

                    {visible && slide.isVideo && slide.videoUrl ? (
                      <button
                        type="button"
                        className="absolute left-1/2 top-1/2 z-10 inline-flex h-16 w-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white text-[#E82127] shadow-[0_18px_45px_rgba(0,0,0,0.18)] transition-transform hover:scale-105 sm:h-20 sm:w-20"
                        onClick={openLightbox}
                        aria-label="Play testimonial video"
                      >
                        <span className="absolute inset-0 -z-10 animate-ping rounded-full bg-[#E82127]/25" />
                        <span className="absolute -inset-3 -z-20 rounded-full border border-[#E82127]/30" />
                        <Play className="ml-1 h-6 w-6 fill-current sm:h-7 sm:w-7" />
                      </button>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="mx-auto flex w-full max-w-2xl flex-col lg:mx-0 lg:min-h-[360px]">
            <div
              ref={quoteMarkRef}
              className={cn(
                'flex aspect-square w-[60px] items-start text-[3rem] font-black leading-none',
                styles.quoteMarkClassName,
              )}
              aria-hidden={quoteIconAlt ? undefined : true}
            >
              {quoteIconUrl ? (
                <NextImage
                  src={quoteIconUrl}
                  alt={quoteIconAlt}
                  width={100}
                  height={100}
                  className={cn('h-12 w-auto object-contain sm:h-16', styles.quoteIconClassName)}
                />
              ) : (
                '“'
              )}
            </div>

            {/* Motion wrapper is separate from clamp target so GSAP never breaks overflow measure. */}
            <div ref={quoteMotionRef}>
              <div
                ref={quoteRef}
                className={cn(
                  'text-[20px] font-medium leading-[1.65] [&_b]:font-bold [&_p]:m-0 [&_p]:inline [&_strong]:font-bold',
                  styles.quoteClassName,
                  !isQuoteExpanded && 'overflow-hidden',
                )}
                style={
                  isQuoteExpanded
                    ? undefined
                    : {
                        maxHeight: QUOTE_COLLAPSED_MAX_HEIGHT,
                      }
                }
                // Avoid hydration mismatch: render plain text until mount, then CMS HTML.
                {...(isMounted
                  ? { dangerouslySetInnerHTML: { __html: activeSlide.quoteHtml } }
                  : { children: activeSlide.quoteHtml })}
              />

              {canExpandQuote || isQuoteExpanded ? (
                <button
                  type="button"
                  className={cn(
                    'mt-1.5 w-fit text-xs font-medium tracking-wide transition-colors',
                    styles.expandButtonClassName,
                  )}
                  onClick={() => setIsQuoteExpanded((current) => !current)}
                  aria-expanded={isQuoteExpanded}
                >
                  {isQuoteExpanded ? 'Show Less' : 'Show More'}
                </button>
              ) : null}
            </div>

            <div ref={authorRef} className="mt-8 sm:mt-10">
              <p
                className={cn(
                  'text-2xl font-bold leading-tight sm:text-[1.7rem]',
                  styles.authorNameClassName,
                )}
              >
                {activeSlide.name}
              </p>
              {activeSlide.role ? (
                <p
                  className={cn(
                    'mt-2 text-base leading-relaxed sm:text-lg',
                    styles.authorRoleClassName,
                  )}
                >
                  {roleParts.title}
                  {roleParts.company ? (
                    <>
                      <span className={cn('mx-2', styles.authorRoleSeparatorClassName)}>·</span>
                      <span className={cn('font-medium', styles.authorCompanyClassName)}>
                        {roleParts.company}
                      </span>
                    </>
                  ) : null}
                </p>
              ) : null}
            </div>

            {hasMultipleSlides ? (
              <div
                ref={controlsRef}
                className="mt-10 flex flex-wrap items-center gap-3 sm:mt-14 lg:mt-8"
              >
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className={cn(
                    'h-14 w-14 rounded-full border transition-colors',
                    styles.controlButtonClassName,
                    'hover:border-[#F0182B] focus-visible:border-[#F0182B] active:border-[#F0182B]',
                  )}
                  onClick={() => goToSlide(activeIndex - 1)}
                  aria-label="Previous testimonial"
                >
                  <ChevronLeft className="h-6 w-6" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className={cn(
                    'h-14 w-14 rounded-full border transition-colors',
                    styles.controlButtonClassName,
                    'hover:border-[#F0182B] focus-visible:border-[#F0182B] active:border-[#F0182B]',
                  )}
                  onClick={() => goToSlide(activeIndex + 1)}
                  aria-label="Next testimonial"
                >
                  <ChevronRight className="h-6 w-6" />
                </Button>

                <div className="ml-2 flex items-center gap-2">
                  {slides.map((slide, index) => (
                    <button
                      key={`${slide.id}-dot`}
                      type="button"
                      aria-label={`Show testimonial ${index + 1}`}
                      aria-current={index === activeIndex ? 'true' : undefined}
                      onClick={() => goToSlide(index)}
                      className="inline-flex items-center justify-center p-[16px] -m-[16px]"
                    >
                      <span
                        aria-hidden
                        className={cn(
                          'block h-2.5 rounded-full transition-all duration-300',
                          index === activeIndex
                            ? 'w-10 bg-[#E82127]'
                            : cn('w-2.5', styles.inactiveDotClassName),
                        )}
                      />
                    </button>
                  ))}
                </div>

                <span
                  className={cn(
                    'ml-auto text-sm font-semibold tracking-[0.08em] sm:text-base',
                    styles.counterClassName,
                  )}
                >
                  {String(activeIndex + 1).padStart(2, '0')} /{' '}
                  {String(totalSlides).padStart(2, '0')}
                </span>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {isMounted && isLightboxOpen && activeSlide.videoUrl
        ? createPortal(
            <div
              className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 px-4 backdrop-blur-sm sm:px-6"
              onClick={closeLightbox}
              role="dialog"
              aria-modal="true"
              aria-label={`${activeSlide.name} testimonial video`}
            >
              <button
                type="button"
                onClick={closeLightbox}
                className="absolute right-4 top-4 z-10 inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20 sm:right-6 sm:top-6"
                aria-label="Close video"
              >
                <X className="h-5 w-5" />
              </button>

              <div
                className="relative w-full max-w-5xl overflow-hidden rounded-2xl bg-black shadow-2xl"
                onClick={(event) => event.stopPropagation()}
              >
                <div className="relative aspect-video w-full">
                  <video
                    ref={lightboxVideoRef}
                    key={activeSlide.id}
                    src={activeSlide.videoUrl}
                    className="absolute inset-0 h-full w-full bg-black object-contain"
                    controls
                    playsInline
                    autoPlay
                    preload="auto"
                    poster={MEDIA_PLACEHOLDER}
                  />
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}
    </section>
  );
}
